/**
 * Product Mapper Utility
 * Handles mapping between frontend and backend data structures
 */

/**
 * Maps frontend form data to backend API format
 * @param {Object} frontendData - Form data from frontend
 * @returns {Object} - Backend API format object
 */
export const mapFrontendToBackend = (frontendData) => {
  // Handle both old and new field name formats
  const mapped = {
    name: frontendData.name || frontendData.Name || '',
    description: frontendData.description || frontendData.Description || '',
    barcode: frontendData.barcode || frontendData.barCode || '',
    categoryId: parseInt(frontendData.categoryId || frontendData.CategoryId || 0),
    brandId: frontendData.brandId || frontendData.brendId || null,
    unit: frontendData.unit || frontendData.Unit || 'pcs',
    originCountry: frontendData.originCountry || frontendData.origin || '',
    isActive: frontendData.isActive !== undefined ? frontendData.isActive : true,
    isFeatured: frontendData.isFeatured !== undefined ? frontendData.isFeatured : false,
    
    // Merchant-specific fields
    price: parseFloat(frontendData.price || frontendData.Price || 0),
    discountPercent: parseFloat(frontendData.discountPercent || frontendData.DiscountPercent || 0),
    minOrderQty: parseInt(frontendData.minOrderQty || frontendData.MinOrderQty || 1),
    stockQuantity: parseInt(frontendData.stockQuantity || frontendData.StockQuantity || 0),
    isAvailable: frontendData.isAvailable !== undefined ? frontendData.isAvailable : true,
  };

  // Handle images
  if (frontendData.imageUrls || frontendData.ImageUrls) {
    const imageArray = frontendData.imageUrls || frontendData.ImageUrls || [];
    if (imageArray.length > 0) {
      mapped.imageUrl = imageArray[0]; // First image as main
      mapped.images = imageArray; // All images as array
    }
  }

  // Clean up null/undefined brandId
  if (mapped.brandId) {
    mapped.brandId = parseInt(mapped.brandId);
  } else {
    delete mapped.brandId;
  }

  return mapped;
};

/**
 * Maps backend response to frontend format
 * @param {Object} backendData - Data from backend API
 * @returns {Object} - Frontend format object with duplicate field names for component compatibility
 */
export const mapBackendToFrontend = (backendData) => {
  const product = backendData.product || backendData;
  const productMerchant = backendData.productMerchant || backendData;

  return {
    id: product.id,
    name: product.name,
    Name: product.name, // Duplicate for component compatibility
    description: product.description || '',
    Description: product.description || '',
    barcode: product.barcode || '',
    barCode: product.barcode || '',
    categoryId: product.categoryId,
    CategoryId: product.categoryId,
    brandId: product.brandId || null,
    brendId: product.brandId || null, // Note: typo kept for component compatibility
    unit: product.unit || 'pcs',
    Unit: product.unit || 'pcs',
    originCountry: product.originCountry || '',
    origin: product.originCountry || '',
    isActive: product.isActive,
    isFeatured: product.isFeatured,
    
    // Images
    imageUrl: product.imageUrl || '',
    imageUrls: product.images || (product.imageUrl ? [product.imageUrl] : []),
    ImageUrls: product.images || (product.imageUrl ? [product.imageUrl] : []),
    
    // Product-Merchant relationship data
    productMerchantId: productMerchant.id,
    price: productMerchant.price || 0,
    Price: productMerchant.price || 0,
    discountPercent: productMerchant.discountPercent || 0,
    DiscountPercent: productMerchant.discountPercent || 0,
    minOrderQty: productMerchant.minOrderQty || 1,
    MinOrderQty: productMerchant.minOrderQty || 1,
    stockQuantity: productMerchant.stockQuantity || 0,
    StockQuantity: productMerchant.stockQuantity || 0,
    reservedQty: productMerchant.reservedQty || 0,
    reorderLevel: productMerchant.reorderLevel || 0,
    isAvailable: productMerchant.isAvailable,
    
    // Timestamps
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
  };
};

/**
 * Validates product data before submission
 * @param {Object} data - Product data to validate
 * @returns {Object} - { isValid: boolean, errors: Record<string, string> }
 */
export const validateProductData = (data) => {
  const errors = {};

  // Required fields validation
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Product name is required';
  }

  if (!data.categoryId || data.categoryId === 0) {
    errors.categoryId = 'Category is required';
  }

  if (!data.unit || data.unit.trim() === '') {
    errors.unit = 'Unit is required';
  }

  // Price validation
  const price = parseFloat(data.price);
  if (isNaN(price) || price < 0) {
    errors.price = 'Price must be a positive number';
  }

  // Discount validation
  const discount = parseFloat(data.discountPercent);
  if (isNaN(discount) || discount < 0 || discount > 100) {
    errors.discountPercent = 'Discount must be between 0 and 100';
  }

  // Quantity validation
  const minQty = parseInt(data.minOrderQty);
  if (isNaN(minQty) || minQty < 1) {
    errors.minOrderQty = 'Minimum order quantity must be at least 1';
  }

  const stockQty = parseInt(data.stockQuantity);
  if (isNaN(stockQty) || stockQty < 0) {
    errors.stockQuantity = 'Stock quantity must be a positive number';
  }

  // Barcode validation (if provided)
  if (data.barcode && data.barcode.length > 50) {
    errors.barcode = 'Barcode is too long';
  }

  // Name length validation
  if (data.name && data.name.length > 255) {
    errors.name = 'Product name is too long (max 255 characters)';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Prepares product data for product creation API call
 * @param {Object} formData - Form data
 * @param {number} merchantId - Merchant ID
 * @returns {Object} - Product creation request
 */
export const prepareProductCreationRequest = (formData, merchantId) => {
  const mapped = mapFrontendToBackend(formData);
  
  // Validate and clean barcode (must be 8-14 digits or empty)
  let cleanBarcode = mapped.barcode?.trim() || '';
  if (cleanBarcode && !/^\d{8,14}$/.test(cleanBarcode)) {
    cleanBarcode = ''; // Invalid barcode, set to empty
  }
  
  const request = {
    name: mapped.name,
    description: mapped.description || undefined,
    categoryId: mapped.categoryId,
    unit: mapped.unit,
    originCountry: mapped.originCountry || undefined,
    isActive: mapped.isActive,
    isFeatured: mapped.isFeatured,
    // Required fields for merchant product creation
    merchantId: merchantId,
    price: mapped.price,
    discountPercent: mapped.discountPercent || 0,
    minOrderQty: mapped.minOrderQty || 1,
    stockQuantity: mapped.stockQuantity || 0,
    isAvailable: mapped.isAvailable !== undefined ? mapped.isAvailable : true,
  };
  
  // Add optional barcode only if valid
  if (cleanBarcode) {
    request.barcode = cleanBarcode;
  }
  
  // Add optional brandId only if provided
  if (mapped.brandId && mapped.brandId > 0) {
    request.brandId = mapped.brandId;
  }
  
  // Note: Images are uploaded separately after product creation
  // via the /api/images/upload endpoint using multipart/form-data
  // The product creation endpoint does not accept image data
  
  return request;
};

/**
 * Prepares product-merchant relationship data
 * @param {number} productId - Product ID
 * @param {number} merchantId - Merchant ID
 * @param {Object} formData - Form data
 * @returns {Object} - Product-merchant creation request
 */
export const prepareProductMerchantRequest = (productId, merchantId, formData) => {
  const mapped = mapFrontendToBackend(formData);
  
  return {
    productId,
    merchantId,
    price: mapped.price,
    discountPercent: mapped.discountPercent,
    minOrderQty: mapped.minOrderQty,
    stockQuantity: mapped.stockQuantity,
    isAvailable: mapped.isAvailable,
    isActive: mapped.isActive,
  };
};

/**
 * Formats search results from backend
 * @param {Array} results - Search results from backend
 * @returns {Array} - Formatted search results with duplicate field names
 */
export const formatSearchResults = (results) => {
  if (!Array.isArray(results)) return [];
  
  return results.map(item => ({
    ...item,
    // Duplicate field names for component compatibility
    Name: item.name,
    ImageUrls: item.images || (item.imageUrl ? [item.imageUrl] : []),
    CategoryId: item.categoryId,
  }));
};
